<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import { storeToRefs } from "pinia";

import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/vue/24/outline";

import { format, reservedThrough, sum, totalsOn } from "@/libs/amount.ts";
import { isOn } from "@/libs/features.ts";

import { toggle } from "@/repositories/transactions.ts";

import { useAccount } from "@/stores/account.ts";
import { useReservations } from "@/stores/reservations.ts";
import { useTransactions } from "@/stores/transactions.ts";
import { useDateRange } from "@/components/DateRange/state.ts";
import { displayRows, onEdit } from "@/functions/transactions.ts";
import ItemTransactions from "@/components/ItemTransactions.vue";

import type { ByNameByDay } from "@/types/transaction";

const account = useAccount();
const transactions = useTransactions();
const reservations = useReservations();
const dateRange = useDateRange();

const { initialBalance } = storeToRefs(account);
const { byNameByDay, byDay } = storeToRefs(transactions);
const { incomeTax } = storeToRefs(reservations);
const { days, from, to } = storeToRefs(dateRange);

// A manual month pick can leave the viewed week outside the window; snap it back in.
watch([from, to], () => dateRange.clampWeek(), { immediate: true });

// Gross cash per day; free cash subtracts the cumulative reserved fund.
const totalsPerDay = computed(() =>
  days.value.map((day) => {
    const key = day.format("YYYY-MM-DD");
    const totals = totalsOn(byDay.value, key, initialBalance.value);
    const reserved = reservedThrough(incomeTax.value?.opening ?? 0, incomeTax.value?.by_day, key);

    return {
      ...totals,
      reservations: reserved,
      balance: isOn("income-tax") ? totals.balance - reserved : totals.balance,
    };
  }),
);

const onToggle = async (id: number) => {
  const isDisabled = await toggle(id);

  transactions.toggle(id, isDisabled);
};

const expanded = ref<string[]>([]);

const transactionsOf = (items: ByNameByDay[string]) =>
  days.value.flatMap(
    (day) => items.transactionsPerDay[day.format("YYYY-MM-DD")]?.transactions ?? [],
  );

// v-for object keys type as string | number; normalize once.
const isExpanded = (name: string | number) => expanded.value.includes(String(name));

const toggleExpand = (name: string | number) => {
  const key = String(name);

  expanded.value = expanded.value.includes(key)
    ? expanded.value.filter((n) => n !== key)
    : [...expanded.value, key];
};

// Rows with a single display line (one-off, or one recurring series) open edit directly.
const onRowClick = (name: string | number, items: ByNameByDay[string]) => {
  if (displayRows(transactionsOf(items)).length > 1) {
    toggleExpand(name);

    return;
  }

  onEdit(items.id, items.type);
};
</script>

<template>
  <div class="px-4 sm:px-6 lg:px-8">
    <div class="mt-8 flow-root">
      <div class="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div class="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
          <table class="min-w-full divide-y divide-gray-300">
            <thead>
              <tr class="divide-x divide-gray-200">
                <th class="py-3.5 pr-4 pl-4 text-xs text-gray-500 text-left font-medium uppercase">
                  Naam
                </th>
                <th
                  v-for="day in days"
                  :key="day.format('YYYY-MM-DD')"
                  class="py-3.5 pr-4 pl-4 text-xs text-gray-500 text-left font-medium uppercase"
                  scope="col"
                >
                  {{ day.format("dd D MMM") }}
                </th>
              </tr>
            </thead>

            <tbody class="divide-y divide-gray-200 bg-white">
              <template v-for="(itemsPerDay, name) in byNameByDay" :key="name">
                <tr
                  class="divide-x divide-gray-200 cursor-pointer"
                  @click="onRowClick(name, itemsPerDay)"
                >
                  <th
                    class="py-4 px-4 flex flex-row gap-x-3 items-center text-sm text-left font-semibold whitespace-nowrap text-gray-900"
                  >
                    <input
                      type="checkbox"
                      :checked="itemsPerDay.isEnabled"
                      @click.stop
                      @change="onToggle(itemsPerDay.id)"
                    />
                    {{ name }}
                    <template v-if="displayRows(transactionsOf(itemsPerDay)).length > 1">
                      <ChevronDownIcon v-if="isExpanded(name)" class="size-4 ml-auto shrink-0" />
                      <ChevronRightIcon v-else class="size-4 ml-auto shrink-0" />
                    </template>
                  </th>
                  <td
                    v-for="day in days"
                    :key="day.format('YYYY-MM-DD')"
                    :class="[
                      itemsPerDay.transactionsPerDay[day.format('YYYY-MM-DD')]?.transactions[0]
                        .type === 'expense'
                        ? 'text-red-600'
                        : 'text-gray-900',
                      'py-4 pr-4 pl-4 text-right text-sm font-medium whitespace-nowrap sm:pl-0',
                    ]"
                  >
                    {{
                      format(
                        sum(itemsPerDay.transactionsPerDay[day.format("YYYY-MM-DD")]?.transactions),
                      )
                    }}
                  </td>
                </tr>

                <ItemTransactions
                  v-if="isExpanded(name)"
                  :transactions="transactionsOf(itemsPerDay)"
                  :periods="days"
                  period-format="YYYY-MM-DD"
                />
              </template>

              <tr>
                <th class="py-3.5 pr-4 pl-4 text-xs text-gray-500 text-left font-medium uppercase">
                  Totaal in
                </th>
                <td
                  v-for="(totals, index) in totalsPerDay"
                  :key="days[index].format('YYYY-MM-DD')"
                  class="py-4 pr-4 pl-4 text-right text-sm font-medium whitespace-nowrap text-gray-900 sm:pl-0"
                >
                  {{ format(totals.in) }}
                </td>
              </tr>

              <tr>
                <th class="py-3.5 pr-4 pl-4 text-xs text-gray-500 text-left font-medium uppercase">
                  Totaal uit
                </th>
                <td
                  v-for="(totals, index) in totalsPerDay"
                  :key="days[index].format('YYYY-MM-DD')"
                  class="py-4 pr-4 pl-4 text-right text-sm font-medium whitespace-nowrap text-gray-900 sm:pl-0"
                >
                  {{ format(totals.out) }}
                </td>
              </tr>

              <tr v-if="isOn('income-tax')">
                <th class="py-3.5 pr-4 pl-4 text-xs text-gray-500 text-left font-medium uppercase">
                  Reserveringen
                </th>
                <td
                  v-for="(totals, index) in totalsPerDay"
                  :key="days[index].format('YYYY-MM-DD')"
                  :class="[
                    (totals.reservations || 0) < 0 ? 'text-red-600' : 'text-gray-900',
                    'py-4 pr-4 pl-4 text-right text-sm font-medium whitespace-nowrap sm:pl-0',
                  ]"
                >
                  {{ format(totals.reservations) }}
                </td>
              </tr>

              <tr>
                <th class="py-3.5 pr-4 pl-4 text-xs text-gray-500 text-left font-medium uppercase">
                  {{ isOn("income-tax") ? "Besteedbaar saldo" : "Saldo" }}
                </th>
                <td
                  v-for="(totals, index) in totalsPerDay"
                  :key="days[index].format('YYYY-MM-DD')"
                  :class="[
                    (totals.balance || 0) < 0 ? 'text-red-600' : 'text-gray-900',
                    'py-4 pr-4 pl-4 text-right text-sm font-medium whitespace-nowrap sm:pl-0',
                  ]"
                >
                  {{ format(totals.balance) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
